

import { nanoid } from 'nanoid';
import XLSX from 'xlsx'


export const parseExcel = (file, options={}) => new Promise((resolve, reject) => {
  const isXlsx = file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || file.name.endsWith('.xlsx');
  const isCsv = file.type === "text/csv" || file.name.endsWith('.csv');
  
  if (!isXlsx && !isCsv) {
    reject({status: 'error', msg: 'Invalid file format. Only XLSX and CSV files are accepted'});
    return;
  }
  
  var reader = new FileReader();
  reader.onerror = () => reject({status: 'error', msg: 'Could not read file'});
  reader.readAsArrayBuffer(file);

  reader.onload = function(e) {
    var data = new Uint8Array(reader.result);
    // Reading must not transform dates — every cell comes out a STRING, never a JS Date.
    // CSV: `raw:true` keeps original text (SheetJS otherwise guesses ambiguous DD/MM as
    // US MM/DD, e.g. "1/2/2026" → Jan 2). Excel: `cellNF` populates number formats so the
    // surgical pass below can swap ONLY date cells for their display string (.w) —
    // avoiding SheetJS's timezone-dependent serial→Date conversion, which in a browser at
    // IST shifts 27 Jan → "Jan 26 23:59:50" (a day back). Actual date normalization
    // happens later, in batch-register, with an explicit format + timezone.
    var workbook = XLSX.read(data, {type: 'array', cellText: true, ...(isCsv ? { raw: true } : { cellNF: true })});

    // For CSV, use first sheet; for XLSX, use selected sheet.
    // `thisSheet` is legitimately undefined when a caller passes no selectedSheetName
    // because it only wants `sheetNames` back (the "which sheet?" prompt). Every deref
    // below must therefore be guarded — sheet_to_json already returns [] for undefined.
    var sheetName = isCsv ? workbook.SheetNames[0] : options.selectedSheetName;
    var thisSheet = workbook.Sheets[sheetName];

    let columnTypes = [];
    if(options.getColumnTypes === true && thisSheet){
      const range = XLSX.utils.decode_range(thisSheet['!ref']);
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({r: 1, c: col});
        const cell = thisSheet[cellAddress];
        const type = cell ? cell.t : 's';
        columnTypes.push(type);
      }
    }

    // Excel only — replace each DATE-formatted numeric cell with its display string (.w),
    // leaving numbers/text exactly as read. Pure serial→string arithmetic, no timezone.
    if(!isCsv && thisSheet){
      Object.keys(thisSheet).forEach(cellAddress => {
        if(cellAddress[0] === '!') return; // skip !ref, !merges, etc.
        const cell = thisSheet[cellAddress];
        if(cell.t === 'n' && cell.z && XLSX.SSF.is_date(cell.z)){
          cell.t = 's';
          cell.v = cell.w;
        }
      });
    }

    // No `UTC` option needed — it only affected JS Date cells, which we no longer produce.
    var parsedData = XLSX.utils.sheet_to_json(thisSheet, {header:1});
    resolve({status: 'success', data: parsedData, sheetNames: workbook.SheetNames, columnTypes});
  }
})

//       return promise;
    
//   }


/**
 * this version_2 support is because, in batch register we now have a functionality
 * where the interface automatically generates the appropriate format of the excel.
 * We wanted to make this more userfriendly by adding extra columns like block label,
 * isRequired. Because of this, the number of header rows increases in this case.
 * 
 * Hence this version_2 support, while maintaining backwards compatibility.
 */
export const convertExcelToJson = (parsedData, options = {}) => {
    let tempRegisDataAry = [];
    
    // Check if this is a version_2 format file
    const isVersion2 = parsedData[0]?.[0]?.trim() === 'version_2';
    
    // Determine which row contains headers
    const headerRowIndex = isVersion2 ? 3 : 0; // If version_2, use row 4 (index 3), else use row 1 (index 0)
    // Determine which row to start data from
    const dataStartIndex = isVersion2 ? 4 : 1; // If version_2, start from row 5 (index 4), else start from row 2 (index 1)
    
    // Get headers from the appropriate row
    const headers = parsedData[headerRowIndex]?.map(header => header?.trim()) || [];
    
    // Process data rows
    parsedData.slice(dataStartIndex).forEach((row) => {
      if (row.some(cell => cell)) { // Only process rows that have any data
        let tempObj = {};
        row.forEach((cellValue, columnIndex) => {
          const key = headers[columnIndex];
          if (key) {
            const propertyVal = cellValue?.toString().trim() || '';
            tempObj[key] = propertyVal;
          }
        });
        // Only add objects that aren't empty
        if(options.genUniqueIdAt){
          tempObj = {
            ...tempObj,
            [options.genUniqueIdAt]: nanoid(8)
          }
        }
        if (Object.keys(tempObj).length > 0) {
          tempRegisDataAry.push(tempObj);
        }
      }
    });
    
    return tempRegisDataAry;
  }



